/* eslint-disable max-lines -- temporarily over the 400-line limit due
 * to the `usePropDiffLog` instrumentation block. Diagnostic-only;
 * remove the marker once the render-cascade investigation is closed
 * and the diagnostic call is removed (or pruned to a smaller props
 * bag). */
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  Suspense,
} from "react";
import { useConnectionState, useZero } from "@rocicorp/zero/react";
import { useAuth } from "@workos-inc/authkit-react";
import { Broadcast } from "@/components/ui/icons";
import { Checkbox } from "@/components/ui/checkbox";
import { useCurrentUser } from "@/lib/current-user";
import { MentionPopover } from "./MentionPopover";
import { SlashCommandPopover } from "./SlashCommandPopover";
const LazyGiphyPreview = React.lazy(() => import("./GiphyPreview"));
import { EmojiShortcodePopover } from "./EmojiShortcodePopover";
import { useMessageTypingIndicator } from "./useMessageTypingIndicator";
import { useMentionPopoverState } from "./useMentionPopoverState";
import { useSlashCommandPopoverState } from "./useSlashCommandPopoverState";
import { useEmojiPopoverState } from "./useEmojiPopoverState";
import { FormattingToolbar } from "./FormattingToolbar";
import { TiptapEditor, type TiptapEditorRef } from "./TiptapEditor";
import {
  AttachmentUploader,
  usePendingAttachments,
} from "./AttachmentUploader";
import { validatePastedFiles, showPasteErrors } from "@/lib/clipboard-utils";
import { usePropDiffLog } from "@/lib/use-prop-diff-log";

import { useCanPost } from "./message-input/use-can-post";
import { usePopoverKeynav } from "./message-input/use-popover-keynav";
import { ReplyingToPill } from "./message-input/ReplyingToPill";
import { useMessageSend } from "./message-input/use-message-send";
import {
  detectSlashCommand,
  detectMentionTrigger,
  detectEmojiShortcodeTrigger,
} from "./message-input/detect-popover-trigger";
import { MessageInputLocked } from "./message-input/MessageInputLocked";
import { MessageInputToolbar } from "./message-input/MessageInputToolbar";
import type { MessageInputProps } from "./message-input/message-input-props";
import { useChannelDrafts } from "./message-input/use-channel-drafts";

export const MessageInput = React.memo(function MessageInput({
  channelId,
  channelName,
  isDM,
  threadId,
  workspaceMembers = [],
  onFilesDroppedRef,
  postingRestriction = "everyone",
  externalInsert,
  onSend,
  isCoworkerDm,
  coworkerIds,
  workspaceId,
  coworkerDmUserId,
  replyingToMessageId,
  replyingToMessage,
  onClearReply,
  hidden = false,
}: MessageInputProps) {
  usePropDiffLog(
    "MessageInput",
    {
      channelId,
      channelName,
      isDM,
      threadId,
      workspaceMembers,
      onFilesDroppedRef,
      postingRestriction,
      externalInsert,
      onSend,
      isCoworkerDm,
      coworkerIds,
      workspaceId,
      coworkerDmUserId,
      replyingToMessageId,
      replyingToMessage,
      onClearReply,
      hidden,
    },
    `ch=${channelId?.slice(-6)} thread=${threadId?.slice(-6) ?? "-"} dm=${!!isDM}`,
  );

  const { user } = useCurrentUser();
  const { getAccessToken } = useAuth();
  const zero = useZero();
  const editorRef = useRef<TiptapEditorRef>(null);
  const [editorText, setEditorText] = useState("");
  const drafts = useChannelDrafts(user?.id ?? null);

  // Debounced draft save. `handleEditorChange` fires once per
  // keystroke and a direct `drafts.set` call there would force
  // `getMarkdown()` (full ProseMirror→markdown serialization) on
  // every press. Coalesce into a single save after the user pauses
  // typing. Channel-switch and unmount paths flush the timer
  // synchronously so the outgoing channel's latest content always
  // makes it into the map.
  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelDraftSave = useCallback(() => {
    if (draftSaveTimerRef.current != null) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
  }, []);
  const saveDraftForCurrentChannel = useCallback(() => {
    cancelDraftSave();
    if (!channelId || threadId) return;
    const md = editorRef.current?.getMarkdown() ?? "";
    drafts.set(channelId, md);
  }, [cancelDraftSave, channelId, threadId, drafts]);
  const scheduleDraftSave = useCallback(() => {
    cancelDraftSave();
    draftSaveTimerRef.current = setTimeout(saveDraftForCurrentChannel, 200);
  }, [cancelDraftSave, saveDraftForCurrentChannel]);
  useEffect(() => () => cancelDraftSave(), [cancelDraftSave]);

  // Per-channel composer drafts (localStorage-backed, see
  // `use-channel-drafts.ts`). Gated on `!threadId` because thread
  // composers are a separate concern — they get their own follow-up
  // if/when we extend this. On every channel switch: save the
  // outgoing channel's markdown, then load the incoming channel's
  // saved markdown (or clear). On restore, focus lands at the end
  // of the content so the user can immediately continue typing.
  // Three sentinel states distinguished below:
  //   `undefined` → very first run (no save; effect just primes the ref)
  //   `null`      → no channel selected yet (no save; effect primes)
  //   `string`    → genuine prev→next switch (save outgoing, load incoming)
  const prevDraftChannelIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (threadId) {
      prevDraftChannelIdRef.current = channelId;
      return;
    }
    const prev = prevDraftChannelIdRef.current;
    const next = channelId;
    prevDraftChannelIdRef.current = next;

    // Save outgoing draft — skip on the initial mount (no prev) and
    // when the channel didn't actually change. Cancel any pending
    // debounced save first so we don't double-write the outgoing
    // channel's content; the synchronous save below is authoritative.
    if (prev !== undefined && prev !== null && prev !== next) {
      cancelDraftSave();
      const md = editorRef.current?.getMarkdown() ?? "";
      drafts.set(prev, md);
    }

    // Load incoming draft (empty string clears the editor when the
    // new channel has nothing saved). Always refocus on a real
    // channel switch — the TiptapEditor's `autoFocus` prop only
    // fires on initial mount, and the persistent MessageArea fiber
    // stays mounted across channel switches under the cap=1 multiplex
    // (see CLAUDE.md), so the editor never re-focuses on its own.
    // Without this the "New message → pick person → Chat" flow lands
    // in the channel with the caret somewhere else.
    if (next) {
      const editor = editorRef.current;
      if (editor) {
        const saved = drafts.get(next) ?? "";
        editor.setContentFromMarkdown(saved);
        editor
          .getEditor()
          ?.commands.focus(saved.trim().length > 0 ? "end" : "start");
      }
    }
  }, [channelId, threadId, drafts, cancelDraftSave]);

  // Focus the composer when the user clicks "Reply" on a message. Same
  // multiplex story as the channel-switch effect above — the editor is
  // already mounted and `autoFocus` won't re-fire on a re-render, so
  // we explicitly call into the imperative handle when the reply
  // target appears.
  const prevReplyingRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevReplyingRef.current;
    prevReplyingRef.current = replyingToMessageId ?? null;
    if (replyingToMessageId && replyingToMessageId !== prev) {
      editorRef.current?.getEditor()?.commands.focus("end");
    }
  }, [replyingToMessageId]);

  // Same idea for in-place thread switches: when the user opens thread A,
  // then clicks "Open thread" on a different parent message, ThreadPanel
  // + MessageInput stay mounted while `threadId` swaps under us. The
  // channel-switch effect above bails early when `threadId` is truthy,
  // and the editor's mount-only `autoFocus` already fired, so without
  // this the caret is stranded somewhere outside the editor.
  const prevThreadRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevThreadRef.current;
    prevThreadRef.current = threadId ?? null;
    if (threadId && prev !== undefined && threadId !== prev) {
      editorRef.current?.getEditor()?.commands.focus("end");
    }
  }, [threadId]);

  const { setTyping, clearTyping } = useMessageTypingIndicator({
    user,
    channelId,
  });

  const {
    showMentions,
    setShowMentions,
    setMentionFilter,
    mentionIndex,
    setMentionIndex,
    filteredUsers,
    insertMention,
    triggerMention,
  } = useMentionPopoverState({
    workspaceMembers,
    userId: user?.id,
    editorRef,
    mentionFilter: "",
  });

  const {
    showSlashCommands,
    setShowSlashCommands,
    setSlashFilter,
    slashIndex,
    setSlashIndex,
    filteredSlashCommands,
    insertSlashCommand,
  } = useSlashCommandPopoverState({
    editorRef,
    slashFilter: "",
  });

  const {
    showEmojiShortcodes,
    setShowEmojiShortcodes,
    setEmojiFilter,
    emojiShortcodeIndex,
    setEmojiShortcodeIndex,
    showEmojiPicker,
    setShowEmojiPicker,
    filteredEmojis,
    insertEmoji,
    insertEmojiShortcode,
  } = useEmojiPopoverState({
    editorRef,
  });

  const canPost = useCanPost({
    channelId,
    isDM,
    postingRestriction,
    userId: user?.id,
  });

  const [giphyPreview, setGiphyPreview] = useState<{
    searchTerm: string;
  } | null>(null);
  // Send re-entry guard. Lives in a ref (not state) so flipping mid-send
  // doesn't trigger a render that flips the editor's `disabled` prop and
  // blurs the contenteditable. See `use-message-send.ts` JSDoc.
  const sendingRef = useRef(false);
  const [showFormatting, setShowFormatting] = useState(true);
  const [alsoSendToChannel, setAlsoSendToChannel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastInsertVersionRef = useRef(0);

  // Handle external insert requests from the suggest panel
  useEffect(() => {
    if (
      !externalInsert ||
      externalInsert.version === lastInsertVersionRef.current
    )
      return;
    if (typeof externalInsert.text !== "string" || !externalInsert.text.trim())
      return;
    lastInsertVersionRef.current = externalInsert.version;
    editorRef.current?.setContentFromMarkdown(externalInsert.text);
    setTimeout(() => editorRef.current?.focus(), 0);
  }, [externalInsert]);

  const attachments = usePendingAttachments();
  const { pendingAttachments, addFiles, removeFile, isUploading } = attachments;

  // Auto-focus editor when channel changes
  useEffect(() => {
    editorRef.current?.focus();
  }, [channelId]);

  // Expose addFiles to parent via ref for drag-and-drop
  useEffect(() => {
    if (onFilesDroppedRef) {
      onFilesDroppedRef.current = (files: File[]) => {
        const { valid, errors } = validatePastedFiles(
          files,
          pendingAttachments.length,
        );
        if (valid.length > 0) addFiles(valid);
        showPasteErrors(errors);
      };
    }
    return () => {
      if (onFilesDroppedRef) onFilesDroppedRef.current = null;
    };
  }, [onFilesDroppedRef, pendingAttachments.length, addFiles]);

  const { sendMessage, handleGiphySend, handleGiphyCancel } = useMessageSend({
    channelId,
    channelName,
    isDM,
    threadId,
    replyingToMessageId,
    onClearReply,
    workspaceId,
    isCoworkerDm,
    coworkerDmUserId,
    coworkerIds,
    workspaceMembers,
    user,
    zero,
    getAccessToken,
    editorRef,
    attachments,
    sendingRef,
    alsoSendToChannel,
    setAlsoSendToChannel,
    setGiphyPreview,
    setShowSlashCommands,
    setShowMentions,
    clearTyping,
    onSend,
  });

  const handleEditorChange = useCallback(() => {
    const text = editorRef.current?.getText() ?? "";
    setEditorText(text);
    if (text.trim()) {
      setTyping();
    } else {
      clearTyping();
    }

    // Persist draft for the current channel (channel composer only —
    // thread composers don't save channel-keyed drafts). 200ms
    // debounce so `getMarkdown()` (full ProseMirror→markdown
    // serialization) doesn't run on every keystroke. Empty content
    // is treated as "no draft" inside the hook and deletes the
    // entry, which also handles the post-send clear path:
    // `editorRef.current.clear()` triggers `onUpdate` → here → the
    // debounce fires 200ms later with the cleared content.
    if (channelId && !threadId) {
      scheduleDraftSave();
    }

    const slash = detectSlashCommand(text);
    if (slash) {
      setShowSlashCommands(true);
      setSlashFilter(slash.filter);
      setSlashIndex(0);
      setShowMentions(false);
      return;
    }
    setShowSlashCommands(false);

    // Detect @mentions / :emoji shortcodes using ProseMirror document
    // structure. textBetween replaces atom nodes (mentions) with ￼,
    // so only user-typed @ characters are visible.
    const tiptapEditor = editorRef.current?.getEditor();
    if (tiptapEditor) {
      const { $from } = tiptapEditor.state.selection;
      const textBeforeCursor = $from.parent.textBetween(
        0,
        $from.parentOffset,
        undefined,
        "￼",
      );

      const mention = detectMentionTrigger(textBeforeCursor);
      if (mention) {
        setShowMentions(true);
        setMentionFilter(mention.filter);
        setMentionIndex(0);
        return;
      }

      const emoji = detectEmojiShortcodeTrigger(textBeforeCursor);
      if (emoji) {
        setShowEmojiShortcodes(true);
        setEmojiFilter(emoji.filter);
        setEmojiShortcodeIndex(0);
        setShowMentions(false);
        return;
      }
    }
    setShowMentions(false);
    setShowEmojiShortcodes(false);
  }, [
    setTyping,
    clearTyping,
    setShowSlashCommands,
    setSlashFilter,
    setSlashIndex,
    setShowMentions,
    setMentionFilter,
    setMentionIndex,
    setShowEmojiShortcodes,
    setEmojiFilter,
    setEmojiShortcodeIndex,
    channelId,
    threadId,
    scheduleDraftSave,
  ]);

  usePopoverKeynav({
    hidden,
    giphyPreview,
    onCancelGiphy: handleGiphyCancel,
    showSlashCommands,
    setShowSlashCommands,
    filteredSlashCommands,
    slashIndex,
    setSlashIndex,
    insertSlashCommand,
    showMentions,
    setShowMentions,
    filteredUsers,
    mentionIndex,
    setMentionIndex,
    insertMention,
    showEmojiShortcodes,
    setShowEmojiShortcodes,
    filteredEmojis,
    emojiShortcodeIndex,
    setEmojiShortcodeIndex,
    insertEmojiShortcode,
  });

  const handleFilePaste = useCallback(
    (files: File[]) => {
      const { valid, errors } = validatePastedFiles(
        files,
        pendingAttachments.length,
      );
      if (valid.length > 0) addFiles(valid);
      showPasteErrors(errors);
    },
    [pendingAttachments.length, addFiles],
  );

  const placeholder = isDM
    ? `Message ${channelName || "user"}`
    : `Message #${channelName || "channel"}`;

  // Stable broadcast handler — when `threadId` is set, the previous
  // inline arrow `() => sendMessage({ broadcast: true })` allocated a
  // fresh function ref every render, defeating `TiptapEditor`'s memo.
  // useCallback'd here so the prop ref is stable across non-thread
  // and intra-thread renders alike.
  const handleBroadcastSend = useCallback(
    () => sendMessage({ broadcast: true }),
    [sendMessage],
  );
  const broadcastHandler = threadId ? handleBroadcastSend : undefined;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
      e.target.value = "";
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  // Render-time ref read: FormattingToolbar wants the live tiptap editor
  // instance; the alternative would be to lift the editor instance to
  // state, which fights tiptap's own lifecycle. Pre-existing pattern.
  // eslint-disable-next-line react-hooks/refs
  const editor = editorRef.current?.getEditor() ?? null;
  const hasContent =
    editorText.trim().length > 0 || pendingAttachments.length > 0;

  if (!canPost) {
    return <MessageInputLocked />;
  }

  return (
    <div className="relative">
      {giphyPreview && (
        <Suspense fallback={null}>
          <LazyGiphyPreview
            searchTerm={giphyPreview.searchTerm}
            onSend={handleGiphySend}
            onCancel={handleGiphyCancel}
          />
        </Suspense>
      )}

      {pendingAttachments.length > 0 && (
        <AttachmentUploader
          pendingAttachments={pendingAttachments}
          onAddFiles={addFiles}
          onRemoveFile={removeFile}
        />
      )}

      {threadId && (
        <div className="px-3 pt-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <Checkbox
              checked={alsoSendToChannel}
              onCheckedChange={(checked: boolean | "indeterminate") =>
                setAlsoSendToChannel(checked === true)
              }
            />
            <Broadcast size={14} />
            <span>Also send to #{channelName || "channel"}</span>
          </label>
        </div>
      )}

      {replyingToMessage && onClearReply && (
        <div className="px-3 pt-2 pb-1">
          <ReplyingToPill
            message={replyingToMessage}
            authorName={
              workspaceMembers.find((m) => m.id === replyingToMessage.user_id)
                ?.display_name ?? "user"
            }
            onClear={onClearReply}
          />
        </div>
      )}

      <ConnectionOfflineHint />

      <div className="pl-3 pr-4 pb-4 pt-1" data-message-input>
        <div className="relative rounded-lg p-px border transition-colors duration-150 message-input-dark">
          <div className="relative rounded-[7px] message-input-dark-inner transition-colors">
            {showMentions && (
              <MentionPopover
                users={filteredUsers}
                onSelect={insertMention}
                selectedIndex={mentionIndex}
              />
            )}
            {showSlashCommands && (
              <SlashCommandPopover
                commands={filteredSlashCommands}
                onSelect={insertSlashCommand}
                selectedIndex={slashIndex}
              />
            )}
            {showEmojiShortcodes && (
              <EmojiShortcodePopover
                emojis={filteredEmojis}
                onSelect={insertEmojiShortcode}
                selectedIndex={emojiShortcodeIndex}
              />
            )}

            {showFormatting && <FormattingToolbar editor={editor} />}

            <div className="px-3 pt-1.5">
              <TiptapEditor
                ref={editorRef}
                placeholder={placeholder}
                disabled={isUploading}
                onSend={sendMessage}
                onBroadcastSend={broadcastHandler}
                onChange={handleEditorChange}
                onBlur={clearTyping}
                onFilePaste={handleFilePaste}
                pendingAttachmentCount={pendingAttachments.length}
                mentionPopupOpen={showMentions}
                slashPopupOpen={showSlashCommands}
                emojiPopupOpen={
                  showEmojiShortcodes && filteredEmojis.length > 0
                }
                autoFocus
              />
            </div>

            <MessageInputToolbar
              isBusy={isUploading}
              showFormatting={showFormatting}
              toggleFormatting={() => setShowFormatting(!showFormatting)}
              showEmojiPicker={showEmojiPicker}
              toggleEmojiPicker={() => setShowEmojiPicker(!showEmojiPicker)}
              closeEmojiPicker={() => setShowEmojiPicker(false)}
              insertEmoji={insertEmoji}
              openFilePicker={openFilePicker}
              triggerMention={triggerMention}
              hasContent={hasContent}
              onSend={() => sendMessage()}
            />
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
});

/**
 * Subtle inline hint that surfaces when the Zero socket is not in
 * `connected` state. Composer stays usable — Zero queues mutations
 * optimistically and replays on reconnect — but the user gets visible
 * confirmation that their message hasn't reached the server yet.
 *
 * Rendered as a sibling component so its render is isolated from the
 * memoized parent: the connection-state hook flips frequently during
 * reconnect, and re-rendering this 1-line pill costs nothing whereas
 * re-rendering the full composer would bust TiptapEditor's expensive
 * memo.
 */
function ConnectionOfflineHint() {
  const state = useConnectionState();
  if (state.name === "connected") return null;
  return (
    <div className="px-4 pt-2 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5 rounded-md bg-foreground/5 px-2 py-1">
        <span className="size-1.5 rounded-full bg-destructive/70" />
        Offline — your message will send when the connection comes back.
      </span>
    </div>
  );
}
